import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import CampaignBasicInfoV2 from './sections/CampaignBasicInfoV2';
import ProductServiceBasicV2 from './sections/ProductServiceBasicV2';
import CampaignDetailV2 from './sections/CampaignDetailV2';
import CampaignMissionV2 from './sections/CampaignMissionV2';
import CampaignOtherV2 from './sections/CampaignOtherV2';
import LoadCampaignModal from './LoadCampaignModal';
import CampaignPreviewModal from './CampaignPreviewModal';
import { generateCampaignProposalPdf } from '../../utils/campaignProposalPdf';
import { useAuthStore } from '../../store/authStore';
import { USER_TYPES } from '../../constants/userTypes';

/**
 * Campaign registration form — 5-section "Matchgo" layout.
 *
 * Visual structure derived from frontend/tmp/campaign_register_layout.html.
 * The data model is identical to the previous 3-section version; only
 * the field groupings and shell chrome change.
 *
 * Sidebar: numbered step circles with ✓ on done / solid fill on active,
 * blue accent bar at left of sections, sticky footer on desktop.
 *
 * Section split:
 *   1. Basic        — brandName, campaignName, campaignCategory, clientBrand
 *   2. Product/Svc  — productName, providedValueDescription, visitAddress
 *   3. Detail       — representativeImages, productIntroduction, provisionMethod, additionalNotes, attachments
 *   4. Mission      — contentChannels, publishingConditions, rewardAmount, schedule, contentWritingRequirements
 *   5. Other        — secondarySubmissions, submissionPath, secondarySubmissionGuide
 */
interface CampaignRegistrationFormProps {
  campaignId?: string;
}

type SectionId =
  | 'section-basic'
  | 'section-product'
  | 'section-detail'
  | 'section-mission'
  | 'section-other';

interface SectionDef {
  id: SectionId;
  labelKey: string;
  required: boolean;
}

const SECTIONS: readonly SectionDef[] = [
  { id: 'section-basic', labelKey: 'registration.v2.sectionBasic', required: true },
  { id: 'section-product', labelKey: 'registration.v2.sectionProduct', required: true },
  { id: 'section-detail', labelKey: 'registration.v2.sectionDetail', required: true },
  { id: 'section-mission', labelKey: 'registration.v2.sectionMission', required: true },
  { id: 'section-other', labelKey: 'registration.v2.sectionOther', required: false },
] as const;

const defaultValues: any = {
  // Section 1: Campaign Basic Info
  brandName: '',
  campaignName: '',
  campaignCategory: '',
  clientBrandId: '',

  // Section 2 & 3: Product/Service Info + Detail
  representativeImages: [],
  productIntroduction: '',
  productName: '',
  providedValueDescription: '',
  provisionMethod: '',
  additionalNotes: '',
  visitAddress: '',
  visitLatitude: null,
  visitLongitude: null,
  attachments: [],

  // Section 4: Campaign Mission
  numberOfRecruits: 0,
  contentChannel: '',
  contentChannels: [] as string[],
  rewardAmount: 0,
  instagramCollaboratorId: '',
  blogInsertLink: '',
  youtubeInsertLink: '',
  publishingConditions: {
    instagram: { collaboratorId: '', conditions: [] },
    blog: { insertLink: '', conditions: [] },
    youtube: { insertLink: '', conditions: [] },
  },
  recruitmentPeriod: { startDate: '', endDate: '', alwaysRecruiting: false },
  announcementStartDate: '',
  announcementEndDate: '',
  alwaysOpening: false,
  visitStartDate: '',
  visitEndDate: '',
  uploadDeadlineDays: 7,
  discussSchedule: false,
  contentWritingRequirements: '',

  // Section 5: Other
  secondarySubmissions: [],
  submissionPath: '',
  secondarySubmissionGuide: '',
};

const CampaignRegistrationForm: React.FC<CampaignRegistrationFormProps> = ({ campaignId: propCampaignId }) => {
  const { t } = useTranslation('campaigns');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAgency = user?.userType === USER_TYPES.AGENCY;
  const { campaignId: paramCampaignId } = useParams<{ campaignId: string }>();
  const campaignId = propCampaignId || paramCampaignId;
  const isEditMode = !!campaignId;

  const [activeSection, setActiveSection] = useState<SectionId>(SECTIONS[0].id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(isEditMode);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Click-initiated smooth scrolls temporarily suspend the "pin to last
  // section at page bottom" fallback so the listener can't overwrite the
  // click-target while the user is still scrolling away from the bottom.
  const clickScrollLockRef = useRef(0);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<any>({
    defaultValues,
    mode: 'onBlur',
  });

  const DRAFT_KEY = `campaign-draft-${user?.userId || 'anon'}`;

  // Save draft to localStorage
  const handleSaveDraft = () => {
    const currentData = watch();
    localStorage.setItem(DRAFT_KEY, JSON.stringify(currentData));
    toast.success(t('registration.v2.draftSaved'));
  };

  // Restore draft from localStorage on mount (only for new campaigns)
  const draftRestoredRef = useRef(false);
  useEffect(() => {
    if (isEditMode || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.brandName || parsed.campaignName || parsed.productName) {
          reset({ ...defaultValues, ...parsed });
          toast.success(t('registration.v2.draftRestored'));
        }
      } catch {
        /* ignore corrupt data */
      }
    }
  }, []);

  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  // Fetch existing campaign data in edit mode
  useEffect(() => {
    if (!campaignId) return;

    const fetchCampaign = async () => {
      setIsLoadingCampaign(true);
      try {
        const response = await graphqlApi.getCampaign(campaignId);
        if (response.success && response.data) {
          const campaign = response.data as any;
          const {
            campaignId: _cid,
            hostId: _hid,
            createdBy: _cb,
            status: _s,
            createdAt: _ca,
            updatedAt: _ua,
            isVerified: _iv,
            visibility: _v,
            __typename: _t,
            ...formData
          } = campaign;
          const clean = JSON.parse(
            JSON.stringify(formData, (key, value) => {
              if (key === '__typename') return undefined;
              if (value === null) return undefined;
              return value;
            }),
          );
          if ((!clean.contentChannels || clean.contentChannels.length === 0) && clean.contentChannel) {
            clean.contentChannels = [clean.contentChannel];
          }
          if (
            !clean.publishingConditions ||
            (!clean.publishingConditions.instagram &&
              !clean.publishingConditions.blog &&
              !clean.publishingConditions.youtube)
          ) {
            clean.publishingConditions = {
              instagram: { collaboratorId: clean.instagramCollaboratorId || '', conditions: [] },
              blog: { insertLink: clean.blogInsertLink || '', conditions: [] },
              youtube: { insertLink: clean.youtubeInsertLink || '', conditions: [] },
            };
          }
          reset({ ...defaultValues, ...clean });
        } else {
          toast.error(t('registration.notFound') || t('messages.campaignNotFound'));
          navigate('/campaigns');
        }
      } catch (error: any) {
        console.error('Error fetching campaign for edit:', error);
        toast.error(error.message || t('registration.notFound') || t('messages.loadingError'));
        navigate('/campaigns');
      } finally {
        setIsLoadingCampaign(false);
      }
    };

    fetchCampaign();
  }, [campaignId, reset, navigate, t]);

  // IntersectionObserver to track which section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          const mostVisible = visibleEntries.reduce((prev, curr) =>
            curr.intersectionRatio > prev.intersectionRatio ? curr : prev,
          );
          setActiveSection(mostVisible.target.id as SectionId);
        }
      },
      {
        rootMargin: '-80px 0px -50% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    SECTIONS.forEach((section) => {
      const el = sectionRefs.current[section.id];
      if (el) observer.observe(el);
    });

    // When the user scrolls to the very bottom of the page, the last
    // section is shorter than the observer's detection band (top 50%)
    // so it never activates via intersection alone. Force the last
    // section active once we're within 2px of the page bottom.
    //
    // Suppressed during click-initiated scrolls so the listener can't
    // overwrite the target section while smooth-scroll is still near
    // the bottom (see scrollToSection below).
    const onScroll = () => {
      if (Date.now() < clickScrollLockRef.current) return;
      const scrolledToBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;
      if (scrolledToBottom) {
        setActiveSection(SECTIONS[SECTIONS.length - 1].id);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const el = sectionRefs.current[sectionId];
    if (el) {
      // Set the active section synchronously on click. Without this, the
      // observer + bottom-of-page scroll listener can race during the
      // smooth scroll — if the user was pinned to the last section by the
      // bottom-scroll listener, the listener keeps firing `section-other`
      // on the first scroll ticks and the click is never reflected in the
      // sidebar. Setting state here makes the click authoritative, and
      // the lock below suspends the bottom-pin override for 800ms so the
      // smooth scroll has time to leave the bottom edge.
      setActiveSection(sectionId as SectionId);
      clickScrollLockRef.current = Date.now() + 800;
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Map each form field to the section that owns it so the first error
  // jumps the user to the right card after submit validation fails.
  const fieldToSection: Record<string, SectionId> = {
    brandName: 'section-basic',
    campaignName: 'section-basic',
    campaignCategory: 'section-basic',
    clientBrandId: 'section-basic',
    productName: 'section-product',
    providedValueDescription: 'section-product',
    visitAddress: 'section-product',
    representativeImages: 'section-detail',
    productIntroduction: 'section-detail',
    provisionMethod: 'section-detail',
    additionalNotes: 'section-detail',
    attachments: 'section-detail',
    contentChannels: 'section-mission',
    publishingConditions: 'section-mission',
    rewardAmount: 'section-mission',
    recruitmentPeriod: 'section-mission',
    announcementStartDate: 'section-mission',
    announcementEndDate: 'section-mission',
    visitStartDate: 'section-mission',
    visitEndDate: 'section-mission',
    uploadDeadlineDays: 'section-mission',
    contentWritingRequirements: 'section-mission',
    secondarySubmissions: 'section-other',
    submissionPath: 'section-other',
    secondarySubmissionGuide: 'section-other',
  };

  const scrollToFirstError = useCallback(() => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;
    const first = errorFields[0];
    const sectionId = fieldToSection[first];
    if (sectionId) scrollToSection(sectionId);
  }, [errors, scrollToSection]);

  const onSubmit = async (data: any) => {
    const selectedChannels: string[] = Array.isArray(data.contentChannels)
      ? data.contentChannels
      : [];
    if (selectedChannels.length === 0) {
      toast.error(
        t('registration.v2.errors.noChannels', {
          defaultValue: '콘텐츠 발행 채널을 1개 이상 선택해주세요.',
        }),
      );
      scrollToSection('section-mission');
      return;
    }

    const pc = data.publishingConditions || {};
    const missingChannels: string[] = [];
    const incompleteChannels: string[] = [];
    const channelLabelKey: Record<string, string> = {
      instagram: 'registration.v2.channels.instagram',
      blog: 'registration.v2.channels.blog',
      youtube: 'registration.v2.channels.youtube',
    };
    selectedChannels.forEach((ch) => {
      const conds = pc?.[ch]?.conditions;
      if (!Array.isArray(conds) || conds.length === 0) {
        missingChannels.push(ch);
        return;
      }
      const anyIncomplete = conds.some(
        (c: any) => !c?.item || !(Number(c?.quantity) >= 1),
      );
      if (anyIncomplete) incompleteChannels.push(ch);
    });

    if (missingChannels.length > 0 || incompleteChannels.length > 0) {
      const labels = (arr: string[]) => arr.map((c) => t(channelLabelKey[c] || c)).join(', ');
      if (missingChannels.length > 0) {
        toast.error(
          t('registration.v2.errors.missingConditions', {
            defaultValue: '{{channels}} 채널의 발행 조건을 1개 이상 추가해주세요.',
            channels: labels(missingChannels),
          }),
        );
      } else {
        toast.error(
          t('registration.v2.errors.incompleteConditions', {
            defaultValue: '{{channels}} 채널의 조건 항목/수량을 모두 입력해주세요.',
            channels: labels(incompleteChannels),
          }),
        );
      }
      scrollToSection('section-mission');
      return;
    }

    setIsSubmitting(true);
    try {
      if (!data.recruitmentPeriod) {
        data.recruitmentPeriod = { startDate: '', endDate: '', alwaysRecruiting: false };
      }
      if (data.recruitmentPeriod.alwaysRecruiting == null) {
        data.recruitmentPeriod.alwaysRecruiting = false;
      }
      if (data.recruitmentPeriod.alwaysRecruiting) {
        data.recruitmentPeriod.endDate = null;
      }

      const cleanData = JSON.parse(
        JSON.stringify(data, (key, value) => {
          if (key === '__typename') return undefined;
          return value;
        }),
      );

      if (isEditMode && campaignId) {
        const result = await graphqlApi.updateCampaign(campaignId, cleanData as any);
        if (result.success) {
          toast.success(t('registration.updateSuccess'));
          navigate(`/campaigns/${campaignId}/detail`);
        } else {
          toast.error(result.error || t('registration.updateError'));
        }
      } else {
        const result = await graphqlApi.createCampaign(cleanData as any);
        if (result.success) {
          clearDraft();
          toast.success(t('registration.createSuccess'));
          const newCampaignId = result.data?.campaignId;
          navigate(newCampaignId ? `/campaigns/${newCampaignId}/detail` : '/campaigns');
        } else {
          toast.error(result.error || t('registration.createError'));
        }
      }
    } catch (error: any) {
      toast.error(
        error.message || (isEditMode ? t('registration.updateError') : t('registration.createError')),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = () => {
    scrollToFirstError();
  };

  const handleLoadCampaign = (campaign: any) => {
    const {
      campaignId: _id,
      hostId: _hid,
      createdBy: _cb,
      status: _s,
      createdAt: _ca,
      updatedAt: _ua,
      isVerified: _iv,
      visibility: _v,
      __typename,
      ...cloneData
    } = campaign;
    const clean = JSON.parse(
      JSON.stringify(cloneData, (key, value) => {
        if (key === '__typename') return undefined;
        if (value === null) return undefined;
        return value;
      }),
    );
    if ((!clean.contentChannels || clean.contentChannels.length === 0) && clean.contentChannel) {
      clean.contentChannels = [clean.contentChannel];
    }
    reset({ ...defaultValues, ...clean });
    toast.success(t('registration.v2.campaignLoaded'));
  };

  const setSectionRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  if (isLoadingCampaign) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner />
      </div>
    );
  }

  // Index of the currently active section (for done/active sidebar states)
  const activeIndex = SECTIONS.findIndex((s) => s.id === activeSection);

  return (
    <div className="max-w-[1040px] mx-auto px-4 sm:px-5 lg:px-6 pt-7 pb-20">
      {/* Back link */}
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 mb-2 transition-colors"
      >
        <ArrowLeftIcon className="w-3.5 h-3.5" />
        {t('registration.backToList')}
      </Link>

      {/* Page Title */}
      <h1 className="text-xl font-bold text-gray-900 tracking-tight mb-6">
        {campaignId ? t('registration.editPageTitle') : t('registration.pageTitle')}
      </h1>

      <div className="lg:grid lg:grid-cols-[210px_1fr] lg:gap-7">
        {/* Sidebar — Desktop (≥1024px) */}
        <nav className="hidden lg:block" aria-label="Form sections">
          <div className="sticky top-20">
            <ul className="mb-5">
              {SECTIONS.map((section, index) => {
                const isActive = activeSection === section.id;
                const isDone = index < activeIndex;
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full flex items-center gap-2.5 pl-3.5 pr-0 py-2.5 text-xs font-medium text-left transition-all border-l-2 ${
                        isActive
                          ? 'text-blue-600 border-l-blue-600 font-bold'
                          : isDone
                          ? 'text-gray-600 border-l-blue-600'
                          : 'text-gray-500 border-l-gray-200 hover:text-gray-700 hover:border-l-gray-400'
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center w-5.5 h-5.5 rounded-full text-[11px] font-bold flex-shrink-0 transition-all ${
                          isActive
                            ? 'bg-blue-600 text-white ring-[3px] ring-blue-100'
                            : isDone
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                        style={{ width: '22px', height: '22px' }}
                      >
                        {isDone ? (
                          <CheckIcon className="w-3 h-3" strokeWidth={3} />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <span className="leading-tight">{t(section.labelKey)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Action buttons (load / draft / preview / convert / submit) */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowLoadModal(true)}
                className="w-full py-2 px-3 text-xs font-medium text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
              >
                {t('registration.loadCampaign')}
              </button>
              <button
                type="button"
                onClick={handleSaveDraft}
                className="w-full py-2 px-3 text-xs font-medium text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
              >
                {t('registration.saveDraft')}
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="w-full py-2 px-3 text-xs font-medium text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
              >
                {t('registration.preview')}
              </button>
              {isAgency && (
                <button
                  type="button"
                  onClick={() => generateCampaignProposalPdf(watch(), t)}
                  className="w-full py-2 px-3 text-xs font-medium text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
                >
                  {t('registration.convertProposal')}
                </button>
              )}
              <button
                type="submit"
                form="campaign-registration-form"
                disabled={isSubmitting}
                className={`w-full py-2 px-3 text-xs font-semibold text-white rounded-md transition-colors ${
                  isSubmitting
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? t('registration.submitting') : t('registration.submitCampaign')}
              </button>
            </div>
          </div>
        </nav>

        {/* Horizontal tab bar — Tablet (768-1023px) */}
        <div className="hidden md:block lg:hidden w-full mb-4 sticky top-16 z-10 bg-white border-b border-gray-200 -mx-4 px-4">
          <div className="flex overflow-x-auto gap-1.5 py-2 scrollbar-hide">
            {SECTIONS.map((section, index) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-blue-600 text-white' : 'bg-white text-gray-500'
                    }`}
                  >
                    {index + 1}
                  </span>
                  {t(section.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main form */}
        <form
          id="campaign-registration-form"
          onSubmit={handleSubmit(onSubmit, onError)}
          className="min-w-0"
        >
          {/* Section 1: 캠페인 기본 정보 */}
          <SectionCard
            id="section-basic"
            innerRef={setSectionRef('section-basic')}
            stepNumber={1}
            title={t('registration.v2.sectionBasic')}
            isActive={activeSection === 'section-basic'}
          >
            <CampaignBasicInfoV2 control={control} setValue={setValue} watch={watch} />
          </SectionCard>

          {/* Section 2: 제공 제품/서비스 정보 */}
          <SectionCard
            id="section-product"
            innerRef={setSectionRef('section-product')}
            stepNumber={2}
            title={t('registration.v2.sectionProduct')}
            isActive={activeSection === 'section-product'}
          >
            <ProductServiceBasicV2 control={control} setValue={setValue} watch={watch} />
          </SectionCard>

          {/* Section 3: 상세정보 */}
          <SectionCard
            id="section-detail"
            innerRef={setSectionRef('section-detail')}
            stepNumber={3}
            title={t('registration.v2.sectionDetail')}
            isActive={activeSection === 'section-detail'}
          >
            <CampaignDetailV2 control={control} setValue={setValue} watch={watch} />
          </SectionCard>

          {/* Section 4: 캠페인 미션 */}
          <SectionCard
            id="section-mission"
            innerRef={setSectionRef('section-mission')}
            stepNumber={4}
            title={t('registration.v2.sectionMission')}
            isActive={activeSection === 'section-mission'}
          >
            <CampaignMissionV2 control={control} watch={watch} setValue={setValue} />
          </SectionCard>

          {/* Section 5: 기타 */}
          <SectionCard
            id="section-other"
            innerRef={setSectionRef('section-other')}
            stepNumber={5}
            title={t('registration.v2.sectionOther')}
            isActive={activeSection === 'section-other'}
          >
            <CampaignOtherV2 control={control} watch={watch} />
          </SectionCard>

          {/* Bottom CTA bar (desktop) */}
          <div className="hidden lg:flex sticky bottom-0 justify-end gap-2.5 px-6 py-4 bg-white border-t border-gray-200 rounded-b-xl -mt-5">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-5 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-100"
            >
              {t('registration.saveDraft')}
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="px-5 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-100"
            >
              {t('registration.preview')}
            </button>
            <button
              type="submit"
              form="campaign-registration-form"
              disabled={isSubmitting}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-md border ${
                isSubmitting
                  ? 'bg-blue-400 border-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 border-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? t('registration.submitting') : t('registration.submitCampaign')}
            </button>
          </div>
        </form>
      </div>

      {/* Load Campaign Modal */}
      <LoadCampaignModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        onSelect={handleLoadCampaign}
      />

      {/* Preview Modal */}
      <CampaignPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        data={watch()}
      />

      {/* Mobile/Tablet sticky bottom submit bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30 flex gap-2">
        <button
          type="button"
          onClick={handleSaveDraft}
          className="flex-1 px-3 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('registration.saveDraft')}
        </button>
        <button
          type="submit"
          form="campaign-registration-form"
          disabled={isSubmitting}
          className={`flex-1 px-3 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${
            isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isSubmitting ? t('registration.submitting') : t('registration.submitCampaign')}
        </button>
      </div>
    </div>
  );
};

/**
 * Card shell for a single registration section. Kept local — the styling
 * is specific to this page and doesn't need to be reused elsewhere yet.
 */
interface SectionCardProps {
  id: SectionId;
  innerRef: (el: HTMLDivElement | null) => void;
  stepNumber: number;
  title: string;
  isActive: boolean;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
  id,
  innerRef,
  stepNumber,
  title,
  isActive,
  children,
}) => (
  <div
    id={id}
    ref={innerRef}
    className={`bg-white rounded-xl border border-gray-200 shadow-sm mb-5 overflow-hidden transition-all ${
      isActive
        ? 'border-l-4 border-l-blue-600 shadow-[0_4px_20px_rgba(59,91,219,0.10)]'
        : 'border-l-4 border-l-gray-200'
    }`}
  >
    <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-center gap-2.5">
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold transition-all ${
          isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
        }`}
      >
        {stepNumber}
      </span>
      <h2 className="text-[15px] font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

export default CampaignRegistrationForm;
